import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { 
  Save, 
  X, 
  Upload, 
  Calendar, 
  Tag, 
  DollarSign, 
  FileText, 
  Loader2,
  Image,
  Paperclip
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { journalAPI } from '../../services/api';

const JournalEntryForm = ({ entry, onSubmit, onCancel, onEntrySaved }) => {
  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const entryTypes = [
    { value: 'trade', label: 'Trade Analysis' },
    { value: 'strategy', label: 'Strategy Note' },
    { value: 'market', label: 'Market Observation' },
    { value: 'lesson', label: 'Lesson Learned' },
    { value: 'goal', label: 'Goal Setting' },
    { value: 'general', label: 'General Note' },
  ];

  const moodOptions = [
    { value: 'excited', label: 'Excited' },
    { value: 'confident', label: 'Confident' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'cautious', label: 'Cautious' },
    { value: 'frustrated', label: 'Frustrated' },
    { value: 'disappointed', label: 'Disappointed' },
  ];

  useEffect(() => {
    if (entry) {
      reset({
        title: entry.title || '',
        content: entry.content || '',
        tags: entry.tags || '',
        is_private: entry.is_private || false,
      });
      setSelectedDate(entry.trade_date ? new Date(entry.trade_date) : null);
      setSelectedType(entryTypes.find(type => type.value === entry.entry_type) || null);
      setSelectedMood(moodOptions.find(mood => mood.value === entry.mood) || null);
      setAttachments(entry.attachments || []);
    } else {
      reset();
      setSelectedDate(null);
      setSelectedType(null);
      setSelectedMood(null);
      setAttachments([]);
    }
  }, [entry, reset]);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const response = await journalAPI.uploadAttachment(file);
        const payload = response.data;
        return {
          id: Date.now() + Math.random(),
          name: payload.name || file.name,
          type: payload.content_type || file.type,
          size: payload.size ?? file.size,
          url: payload.url,
          uploaded_at: payload.uploaded_at || new Date().toISOString()
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setAttachments(prev => [...prev, ...uploadedFiles]);
      toast.success(`${files.length} file(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const onSubmitForm = async (data) => {
    setIsSubmitting(true);
    try {
      const formData = {
        ...data,
        entry_type: selectedType?.value || 'general',
        trade_date: selectedDate ? selectedDate.toISOString().split('T')[0] : null,
        mood: selectedMood?.value || null,
      };

      if (attachments && attachments.length) {
        formData.attachments = attachments;
      }

      await onSubmit?.(formData, entry);
      onEntrySaved?.();
      reset();
      setSelectedDate(null);
      setSelectedType(null);
      setSelectedMood(null);
      setAttachments([]);
    } catch (error) {
      // Parent handles error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {entry ? 'Edit Journal Entry' : 'New Journal Entry'}
        </h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Title *
          </label>
          <input
            type="text"
            id="title"
            {...register('title', { required: 'Title is required' })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Enter journal entry title..."
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title.message}</p>
          )}
        </div>

        {/* Entry Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Entry Type *
          </label>
          <Select
            options={entryTypes}
            value={selectedType}
            onChange={setSelectedType}
            placeholder="Select entry type..."
            className="dark:text-gray-900"
            classNamePrefix="react-select"
          />
        </div>

        {/* Trade Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Calendar className="inline h-4 w-4 mr-1" />
            Trade Date
          </label>
          <DatePicker
            selected={selectedDate}
            onChange={setSelectedDate}
            dateFormat="yyyy-MM-dd"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholderText="Select trade date..."
            isClearable
          />
        </div>

        {/* Mood */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mood
          </label>
          <Select
            options={moodOptions}
            value={selectedMood}
            onChange={setSelectedMood}
            placeholder="Select your mood..."
            isClearable
            className="dark:text-gray-900"
            classNamePrefix="react-select"
          />
        </div>

        {/* Content */}
        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <FileText className="inline h-4 w-4 mr-1" />
            Content
          </label>
          <textarea
            id="content"
            rows={8}
            {...register('content')}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Write your journal entry content here..."
          />
        </div>

        {/* Quantity */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Number of Shares
          </label>
          <input
            type="number"
            id="quantity"
            {...register('quantity', { valueAsNumber: true })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="e.g., 100"
            min={0}
          />
        </div>

        {/* Risk:Reward */}
        <div>
          <label htmlFor="risk_reward" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Risk : Reward
          </label>
          <input
            type="text"
            id="risk_reward"
            {...register('risk_reward')}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="e.g., 1:2"
          />
        </div>

        {/* Profit / Loss */}
        <div>
          <label htmlFor="pnl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Profit / Loss (amount)
          </label>
          <input
            type="number"
            step="0.01"
            id="pnl"
            {...register('pnl', { valueAsNumber: true })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="e.g., 250.50"
          />
        </div>

        {/* Stock Symbol */}
        <div>
          <label htmlFor="stock_symbol" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Symbol
          </label>
          <input
            type="text"
            id="stock_symbol"
            {...register('stock_symbol')}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="e.g., AAPL, TSLA"
          />
        </div>

        {/* Tags */}
        <div>
          <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Tag className="inline h-4 w-4 mr-1" />
            Tags
          </label>
          <input
            type="text"
            id="tags"
            {...register('tags')}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Enter tags separated by commas (e.g., long, short, swing)"
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Paperclip className="inline h-4 w-4 mr-1" />
            Attachments (Screenshots, Documents)
          </label>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              disabled={isUploading}
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center space-y-2"
            >
              {isUploading ? (
                <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              ) : (
                <Upload className="h-8 w-8 text-gray-400" />
              )}
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {isUploading ? 'Uploading...' : 'Click to upload files or drag and drop'}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-500">
                PNG, JPG, PDF, DOC up to 10MB each
              </span>
            </label>
          </div>

          {/* Display uploaded files */}
          {attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Uploaded Files:</h4>
              {attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Image className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{attachment.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {(attachment.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(attachment.id)}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Private Entry */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_private"
            {...register('is_private')}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
          />
          <label htmlFor="is_private" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
            Make this entry private
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !selectedType}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            <span>{entry ? 'Update Entry' : 'Save Entry'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default JournalEntryForm;